import { MultiFetcher } from 'data/FHIRFetcher';
import FHIRUpdater from 'data/FHIRUpdater';
import type {
  Provenance,
  // Aliased: the local default import `Questionnaire` (./questionnaire) is the
  // React component, and would otherwise collide with the FHIR resource type.
  Questionnaire as FhirQuestionnaire,
  QuestionnaireItemAnswerOption,
  QuestionnaireResponse as FhirQuestionnaireResponse,
  QuestionnaireResponseItem,
  QuestionnaireResponseItemAnswer,
  DocumentReference,
  Coding,
  Extension,
  Reference,
  Task,
} from 'fhir/r4';
import { FHIRProxyClient, ProvenanceBuilder } from 'fhir-sdoh';
import { base64ToPdfBlob, stripHtml } from 'fhir-sdoh/common/constants';
import jsPDF from 'jspdf';
import { get } from 'lodash';
import moment from 'moment';
import { useState, useEffect, useMemo } from 'react';
import { DatesRangeInput } from 'semantic-ui-calendar-react';
import {
  Loader,
  List,
  Grid,
  Icon,
  Confirm,
  Divider,
  Message,
  Button,
  Dropdown,
  Header,
  Segment,
  Label,
} from 'semantic-ui-react';
import type { DropdownItemProps, DropdownProps } from 'semantic-ui-react';
import type { ReferralSummary } from 'types/patient-data';
import { mapKey } from 'utils';
import {
  QuestionnaireResponseHelper,
  QuestionnaireHelper,
  QuestionnaireExtensionHelper,
} from 'utils/Helpers';
import type { PractitionerEntry, ResourceEntry } from 'utils/bundleNarrowing';

import { useAuthContext } from '../../../contexts/AuthContext';

import {
  ADDENDUM_FIELD_IDS,
  ALL_COSIGN_GROUP_IDS,
  ALL_COSIGN_PRACTITIONER_IDS,
  EXCLUDED_CHART_GROUPS,
  RD_SIGNATURE_CREDENTIALS_LINK_ID,
  SURVEYS_PDF_EXCLUDED_LINK_IDS,
} from './constants';
import {
  getParentResponseId,
  getOriginalChartId,
  getChainParentId,
  getLastModifiedBy,
  buildIdentifier,
  isReadOnlyAfterCompletion,
  isRdConsultChart,
  isAddendumQuestionnaire,
  hasNpiIdentifier,
  isAwaitingCosign,
  requiresCosignWorkflow,
  getLinkedQuestionnaireResponseChain,
  getCosignPractitionerId,
  isSubmittedForCosign,
  findQuestionnaireResponseAnswerByLinkId,
  flattenQuestionnaireItems,
  findQuestionnaireResponseAnswers,
  indexQuestionnaireResponseAnswers,
} from './fhir-helpers';
import type { LooseQuestionnaireItem, LooseQuestionnaireResponseItem } from './fhir-helpers';
import QuestionnairesList from './list';
import type { DiffAnswer } from './questionDiffItem';
import QuestionDiffItem from './questionDiffItem';
import Questionnaire from './questionnaire';
import type { QuestionnaireBundle, QuestionnaireOption } from './types';
import type { SurveyImageMap } from './types/item-props';
import { useAddendumState } from './useAddendumState';

/**
 * A Questionnaire bundle entry as this component reads it.
 *
 * `resource` is REQUIRED, narrowing `fhir4.BundleEntry` where it is optional.
 * This component reads `entry.resource.id` unguarded throughout; the search
 * bundles come from `/Questionnaire` and `/QuestionnaireResponse` queries,
 * whose every entry carries a resource.
 */
type QuestionnaireEntry = { resource: FhirQuestionnaire };

/** A QuestionnaireResponse bundle entry as this component reads it. */
type QuestionnaireResponseEntry = { resource: FhirQuestionnaireResponse };

/**
 * One row of the survey list, as `createDataObj` below builds it.
 *
 * Every field is derived from a `QuestionnaireResponse` resource, so the
 * optionality is that resource's cardinality rather than a choice made here:
 * `authored`, `questionnaire` and `status` are all `0..1`, and `item` is `0..*`.
 * `authoredOn` is `'' | undefined` when `authored` is absent because the
 * producer's `&&` yields the falsy left operand rather than a formatted date.
 *
 * `header` is `string` by the declared type of the local it is assigned from,
 * but that local is filled with `getTitle()!` — an untitled Questionnaire really
 * does put `undefined` here. The PDF and list sites both `||`-fall back, so the
 * absence is already handled downstream; see the note at the assignment.
 */
type SurveyListRow = {
  authoredOn: string | undefined;
  /** The questionnaire id, sliced out of the `Questionnaire/{id}` canonical. */
  questionnaire: string;
  userName: string;
  questions: FhirQuestionnaireResponse['item'];
  header: string;
  hasTranslation: boolean;
  status: FhirQuestionnaireResponse['status'];
};

/**
 * One question as the PDF writer and the chart-copy renderer flatten it.
 *
 * `linkId` and `text` are `0..1` on `Questionnaire.item`, and both consumers
 * `||`-fall back to a literal, so the absence is real and already handled.
 * `answerOptions` is passed to `displayAnswers`, which is the only reader.
 */
type FlattenedQuestion = {
  linkId: string | undefined;
  text: string | undefined;
  answerOptions: QuestionnaireItemAnswerOption[] | undefined;
};

/**
 * One top-level questionnaire page with its questions flattened out of any
 * intervening groups. Built once per questionnaire into `questionnaires`, and
 * rebuilt ad hoc for addenda by `buildPdfCategoriesFromQuestionnaireResource`.
 */
type FlattenedCategory = {
  linkId: string | undefined;
  text: string | undefined;
  item: FlattenedQuestion[];
};

/**
 * Props for {@link QuestionnaireResponseCard}.
 *
 * Required vs optional follows what the component can run without, not what its
 * single caller (`questionnaires/index.tsx`) passes: `responses.spec.tsx`
 * renders with only the required fields below, so everything else is genuinely
 * optional.
 *
 * The FHIR bundles stay loosely shaped (`entry` arrays of `{ resource }`) —
 * that is all this component reads before handing the resources to the typed
 * helpers.
 */
type QuestionnaireResponseCardProps = {
  QRID?: { isNewResponse?: boolean; id?: string } | string | undefined;
  /**
   * The patient's referrals, forwarded untouched to `questionnaire.tsx` and on
   * to `types/string`'s referral dropdown.
   *
   * SUSPECTED DEFECT — REPORTED, NOT FIXED. The origin in `patient/index.tsx` is
   * `ReferralSummary[]`, whose `id` and `referralId` are both OPTIONAL, while
   * the eventual consumer's `ReferralOptionSource` declares both REQUIRED
   * because they key and value the dropdown option. A referral summary missing
   * either renders an option that cannot be selected. The two ends of this
   * three-layer forward have never agreed; this states the origin's shape,
   * which is the one that is actually passed.
   */
  allReferralData?: ReferralSummary[] | undefined;
  /** The `/CareTeam` search bundle, forwarded untouched. */
  careTeamData?: fhir4.Bundle | undefined;
  consentDataMutate?: (() => void) | undefined;
  doNotContact?: unknown;
  fhirPatient?: unknown;
  /** Natural size of the survey image, used to scale it into the PDF. */
  height?: number | undefined;
  org: string;
  /**
   * `FlatPatient.email` — the raw `fhirpath.evaluate` collection, forwarded
   * untouched to `list.tsx`, which indexes `[0]` to test for presence.
   */
  patientEmail?: unknown[] | undefined;
  patientFirstName?: unknown;
  patientId: string;
  /** `FlatPatient.mobilePhone`, the same raw collection shape as the email. */
  patientPhoneNumber?: unknown[] | undefined;
  /**
   * The active-practitioner list built in `patient/index.tsx` by filtering a
   * `/Practitioner` search through `isResourceEntry('Practitioner')`, so
   * `resource` is a whole `Practitioner` and is required.
   *
   * The previous local spelling declared only `{ id, identifier }` while three
   * sites here read `resource.name` — the two sides of this prop disagreed
   * about the shape, and the reads survived only because the value was `any`.
   */
  practitionerData?: PractitionerEntry[] | undefined;
  profileMutate?: (() => void) | undefined;
  questionnaireData: { entry?: QuestionnaireEntry[] | undefined };
  questionnaireResponseData: { entry?: QuestionnaireResponseEntry[] | undefined };
  /**
   * SWR 0.5.7's `mutate` for the `/QuestionnaireResponse` search, which resolves
   * to `Bundle | undefined`. Declared as the bundle rather than `unknown`
   * because `refreshQA` reads `data.entry[0].resource.id` off the result — the
   * previous `Promise<unknown>` spelling and those reads disagreed.
   */
  questionnaireResponseMutate: () => Promise<fhir4.Bundle | undefined>;
  refreshPatientDetails?: (() => void) | undefined;
  refreshTasks?: (() => void) | undefined;
  setQRID: (id: unknown) => void;
  /**
   * The `/Task` search bundle. Read here only through
   * `getConsultationTaskLabelFromBundle`, and forwarded to `questionnaire.tsx`,
   * which declares `{ entry?: { resource?: Task }[] }` — the same bundle stated
   * with its resource type, which is what this now says too.
   */
  tasksData?: { entry?: { resource?: Task }[] } | null | undefined;
  tasksMutate?: (() => void) | undefined;
  width?: number | undefined;
};

function QuestionnaireResponseCard(props: QuestionnaireResponseCardProps) {
  const authInfo = useAuthContext();
  /**
   * The response being edited. `undefined` when none is open; `duplicateSurvey`
   * and the addendum prefill both put a partially-built resource here (no `id`
   * yet, `item` copied from the source), which is why it is `Partial` rather
   * than a whole `QuestionnaireResponse`.
   */
  const [currentQuestionnaireResponse, setCurrentQuestionnaireResponse] = useState<
    Partial<FhirQuestionnaireResponse> | undefined
  >();
  /**
   * The response bundle this card renders from.
   *
   * Seeded from the prop and replaced by `refreshQA` with SWR's re-fetched
   * bundle for the same `/QuestionnaireResponse` search. Carries the prop's
   * shape — every entry has a resource — because that is what this file's
   * `entry.resource` reads assume throughout; see the note where the re-fetched
   * bundle is narrowed to it.
   *
   * `undefined` is SWR's pre-resolution value and drives the loader arm below.
   */
  const [QuestionnaireResponseData, setQuestionnaireResponseData] = useState<
    { entry?: QuestionnaireResponseEntry[] | undefined } | undefined
  >(props.questionnaireResponseData);
  /**
   * The selected response id, `''` for none.
   *
   * `null` is in the union because `duplicateSurvey` sets it, and `undefined`
   * because the `QRID.id` and `QRHelper.getId()` producers are both `0..1`.
   * Neither non-string value is a valid key: they reach `QRDataObj[mapKey(activeItem)]`
   * as `"null"`/`"undefined"`, which miss, while the `activeItem !== ''` render
   * guards still treat them as a selection. Stated, not changed.
   */
  const [activeItem, setActiveItem] = useState<string | null | undefined>('');
  const [deleteConfirm, setDeleteConfirm] = useState(false);
  const [questionnaireResponseFilter, setQuestionnaireResponseFilter] = useState<{
    title: string | null;
    status: string | null;
  }>({
    title: null,
    status: null,
  });
  const [questionnaireTypesOrg, setQuestionnaireTypesOrg] = useState<QuestionnaireOption[]>([]);
  const [dateState, setDateState] = useState('');
  const [startRange, setStartRange] = useState('');
  const [endRange, setEndRange] = useState('');
  /**
   * The survey list indexed by QuestionnaireResponse id.
   *
   * Rebuilt on every render (it is a plain local, not state), and read with
   * subscripts that can miss — `activeItem` may be `''`, `null`, or an id whose
   * response was filtered out — so the value type includes `undefined`.
   */
  const QRDataObj: Record<string, SurveyListRow | undefined> = {};
  const [surveyLanguage, setSurveyLanguage] = useState('');
  const [surveyImage, setSurveyImage] = useState<SurveyImageMap>({});
  const [containsLang, setContainsLang] = useState(false);
  const [currentQuestionnaire, setCurrentQuestionnaire] = useState<
    FhirQuestionnaire | null | undefined
  >();
  const [loader, setLoader] = useState(false);
  const [imgLoader, setImgLoader] = useState(false);
  const [error, setError] = useState('');
  const addendum = useAddendumState();

  const _currentPractitionerHasNpi = useMemo(() => {
    if (!props.practitionerData || !authInfo.practitionerId) return false;
    const me = props.practitionerData.find((p) => p.resource.id === authInfo.practitionerId);
    return me ? hasNpiIdentifier(me.resource) : false;
  }, [props.practitionerData, authInfo.practitionerId]);

  const languagesCareBetty = [
    { key: 'es', text: 'Español', value: 'es' },
    { key: 'en-us', text: 'English (US)', value: 'en-us' },
    { key: 'so', text: 'Somali', value: 'so' },
  ];

  const questionnaireStatus = ['amended', 'completed', 'in-progress', 'stopped'];

  /**
   * The display text for a node in the caregiver's selected survey language.
   *
   * Called with three different things — a `Questionnaire.item` (page or
   * question), and a bare `Coding` off an `answerOption`/`answer` — which share
   * only `extension`. The `valueCoding` arm is what serves the `Coding` callers;
   * on an item node it is simply absent, and the `?.` there predates this type.
   *
   * The inner `=== undefined` branch is unreachable: it is nested inside a
   * truthiness test of the very same call. It reads `item.valueCoding.display`
   * UNGUARDED, so were it ever entered on an item node it would throw. Recorded
   * as written.
   */
  const getTranslation = (
    item: { extension?: Extension[] | undefined; valueCoding?: Coding | undefined } | undefined,
  ): string | undefined => {
    let translation: string | undefined;
    const extHelper = new QuestionnaireExtensionHelper(item?.extension);

    if (extHelper.getTargetTranslation(surveyLanguage)) {
      if (extHelper.getTargetTranslation(surveyLanguage) === undefined) {
        translation = item!.valueCoding!.display;
      }
      translation = extHelper.getTargetTranslation(surveyLanguage);
    } else {
      translation = item?.valueCoding?.display;
    }

    return translation;
  };

  const resetFilters = () => {
    setQuestionnaireResponseFilter({
      title: null,
      status: null,
    });
    setDateState('');
    setStartRange('');
    setEndRange('');
    setSurveyImage({});
  };

  useEffect(() => {
    setQuestionnaireResponseData(props.questionnaireResponseData);
  }, [props.questionnaireResponseData]);

  // The `?? []` fallback hands the helper an empty ARRAY where a
  // QuestionnaireResponse is expected. That is what runs today — the helper
  // optional-chains every read, so an array simply yields undefined from each
  // accessor — so the cast records the existing value rather than replacing the
  // fallback with `undefined`, which would be a different value reaching it.
  const QRHelper = new QuestionnaireResponseHelper(
    (get(QuestionnaireResponseData, 'entry[0].resource') ?? []) as fhir4.QuestionnaireResponse,
  );

  useEffect(() => {
    if (props.QRID && props.questionnaireData) {
      // `QRID` is declared as an object OR a bare string, but the only producer
      // in the app (patient/index.tsx setQRID) passes the object form. The
      // property reads below are written against that form; on a string they
      // would simply be undefined rather than throw. Narrowing to the shape the
      // code already assumes changes nothing at runtime.
      const QRID = props.QRID as { isNewResponse?: boolean; id?: string };
      if (QRID.isNewResponse && props.questionnaireData.entry) {
        setActiveItem('');

        const survey =
          props.questionnaireData.entry.filter(
            (questionnaire) => questionnaire.resource.id === QRID.id,
          )[0] &&
          props.questionnaireData.entry.filter(
            (questionnaire) => questionnaire.resource.id === QRID.id,
          )[0]!.resource;
        setCurrentQuestionnaire(survey);
      } else {
        setActiveItem(QRID.id);
        setCurrentQuestionnaire(null);
      }
    } else if (QuestionnaireResponseData?.entry) {
      // eslint-disable-next-line @typescript-eslint/no-floating-promises -- fire-and-forget panel load; a rejection leaves the selected item's detail empty with no error shown
      handleItemClick(QRHelper.getId()!);
      setActiveItem(QRHelper.getId());
      // Kept as an expression statement: an `if` would emit different JavaScript.
      // eslint-disable-next-line @typescript-eslint/no-unused-expressions
      QRDataObj[QRHelper.getId()!]?.hasTranslation ? setContainsLang(true) : setContainsLang(false);
    }
  }, [QuestionnaireResponseData, authInfo.token, props.QRID, props.questionnaireData]);

  /** Flattened page/question layout per questionnaire id. Rebuilt each render. */
  const questionnaires: Record<string, FlattenedCategory[] | undefined> = {};

  const handleItemClick = async (name: string) => {
    // Kept as an expression statement: an `if` would emit different JavaScript.
    // eslint-disable-next-line @typescript-eslint/no-unused-expressions
    QRDataObj[name]?.hasTranslation ? setContainsLang(true) : setContainsLang(false);

    const questions = QRDataObj[name]?.questions;
    const valueReferences: (Reference | undefined)[] = [];

    // Kept as an expression statement: an `if` would emit different JavaScript.
    // eslint-disable-next-line @typescript-eslint/no-unused-expressions
    questions &&
      questions.forEach((QAS: LooseQuestionnaireResponseItem) => {
        // Kept as an expression statement: an `if` would emit different JavaScript.
        // eslint-disable-next-line @typescript-eslint/no-unused-expressions
        QAS.item &&
          QAS.item.forEach((QA: LooseQuestionnaireResponseItem) => {
            const answer = QA.answer;

            if (answer !== undefined && answer?.[answer.length - 1]?.valueReference !== undefined) {
              valueReferences.push(answer[answer.length - 1]!.valueReference);
            }
          });
      });

    if (valueReferences?.length) {
      const referenceUrls: string[] = [];

      // `element` is asserted present: the producer above pushes only when the
      // answer's `valueReference !== undefined`, so no undefined reaches here.
      // The `?.` inside is the code's own and is left as written.
      valueReferences.forEach((element) => {
        if (
          element!.reference &&
          !Object.prototype.hasOwnProperty.call(
            surveyImage,
            mapKey(element?.reference?.split('/')[1]),
          )
        ) {
          referenceUrls.push('/' + element!.reference + `?patient=${props.patientId}`);
        }
      });

      if (referenceUrls.length > 0) {
        setImgLoader(true);
        const referenceContents = await MultiFetcher<DocumentReference>(
          authInfo.token,
          ...referenceUrls,
        );
        setImgLoader(false);

        if (referenceContents?.length) {
          const imageContent: SurveyImageMap = {};
          // `MultiFetcher` resolves one slot per URL and its fetcher returns
          // `undefined` for an empty endpoint; no empty URL is built above, so
          // every slot here is a resource. Asserted rather than filtered — a
          // filter would change which references reach `imageContent`.
          referenceContents.forEach((reference) => {
            const content = reference!.content;
            // SUSPECTED DEFECT — REPORTED, NOT FIXED. `DocumentReference.content`
            // is `1..*` in FHIR R4 so `[0]` is safe by the profile, but
            // `attachment.contentType` and `attachment.data` are both `0..1`. A
            // reference whose attachment carries a `url` instead of inline `data`
            // — the normal shape for a large image — yields the string
            // `"data:undefined;base64, undefined"`, so the survey photo renders
            // broken rather than falling back to fetching the url.
            //
            // The two sides also disagree about `contentType`: the shared
            // `SurveyAttachment` declares it REQUIRED `string`, and every
            // consumer compares it to `'application/pdf'` to pick a renderer,
            // while this producer can only supply the `0..1` field. An
            // attachment without one takes the non-PDF `<img>` branch.
            imageContent[mapKey(reference!.id)] = {
              data: `data:${content[0]!.attachment.contentType};base64, ${content[0]!.attachment.data}`,
              contentType: content[0]!.attachment.contentType!,
            };
          });
          setSurveyImage({ ...imageContent, ...surveyImage });
        }
      }
    }

    setActiveItem(name);
    setError('');
  };

  const handleLanguageSelect = (
    _event: React.SyntheticEvent<HTMLElement>,
    { value }: DropdownProps,
  ) => {
    /*
     * SUSPECTED DEFECT — REPORTED, NOT FIXED. `surveyLanguage` is declared
     * `string` and is passed to `QuestionnaireExtensionHelper.getTargetTranslation`
     * as the language code to match. Semantic's `DropdownProps['value']` is the
     * wider `string | number | boolean | (…)[] | undefined`; clearing the
     * language dropdown emits `undefined`, which lands in the state as
     * `undefined` rather than resetting it to `''`. The assertion records the
     * value that reaches the state today.
     */
    setSurveyLanguage(value as string);
  };

  /**
   * `event` is Semantic's `React.SyntheticEvent<HTMLElement>` — the type its
   * `Dropdown.onChange` actually supplies — not the `MouseEvent` this handler
   * previously declared. Nothing mouse-specific is read; only `target`, which
   * both carry.
   */
  const handleQRFilter = (event: React.SyntheticEvent<HTMLElement>, attributeName: string) => {
    setSurveyImage({});
    /*
     * SUSPECTED DEFECT — REPORTED, NOT FIXED. `React.MouseEvent['target']` is
     * `EventTarget`, which carries no `textContent`; only the `Element` subtype
     * does. Every element this handler is bound to is a Semantic menu item, so
     * the read resolves at runtime, but the value is `string | null` — a filter
     * chip whose label is empty puts `null` into the filter and
     * `filterResponses` then reads `null.toLowerCase()`. The two spellings of
     * the filter state (`title`/`status` here, `Record<string, string>` in the
     * old callback annotation) disagreed about exactly this nullability.
     */
    const value = event.target && (event.target as Element).textContent;
    setQuestionnaireResponseFilter((prevState) => ({ ...prevState, [attributeName]: value }));
  };

  const handleChangeDate = (
    _event: React.SyntheticEvent<HTMLElement>,
    { value }: DropdownProps,
  ) => {
    setDateState(value as string);
    /*
     * SUSPECTED DEFECT — REPORTED, NOT FIXED. Semantic's `DropdownProps['value']`
     * is `string | number | boolean | (string|number|boolean)[] | undefined`, and
     * `.split` exists on none of those but `string`. Clearing the date filter
     * emits `undefined` here and throws; the range options this dropdown renders
     * are always `'<start> - <end>'` strings, which is why only the clear path
     * reaches it.
     */
    const temp = (value as string).split(' - ');
    setStartRange(temp[0]!);
    setEndRange(temp[1]!);
  };

  // eslint-disable-next-line @typescript-eslint/require-await
  const handleDownloadClick = async () => {
    try {
      if (!activeItem || !QRDataObj?.[mapKey(activeItem)]) {
        setError('No survey selected to download.');
        return;
      }

      setError('');

      const selectedSurveyForDownload = QRDataObj[mapKey(activeItem)];
      let rawChain: ResourceEntry<'QuestionnaireResponse'>[];
      if (isAddendumQuestionnaire(selectedSurveyForDownload!.questionnaire)) {
        const single = findResponseData(activeItem);
        rawChain = single ? [single] : [];
      } else {
        rawChain = getLinkedQuestionnaireResponseChain(
          activeItem,
          QuestionnaireResponseData?.entry || [],
        );
        if (rawChain.length === 0) {
          const single = findResponseData(activeItem);
          if (single) rawChain = [single];
        }
      }

      const chainEntries = rawChain.filter((e) => {
        const id = (e.resource ?? e).id;
        return id && QRDataObj[id];
      });

      if (chainEntries.length === 0) {
        setError('No survey selected to download.');
        return;
      }

      const getRes = (entry: ResourceEntry<'QuestionnaireResponse'> | FhirQuestionnaireResponse) =>
        'resource' in entry ? entry.resource : entry;
      const rootId = getRes(chainEntries[0]!).id;
      const rootSurvey = QRDataObj[mapKey(rootId)];
      const multiSegment = chainEntries.length > 1;
      const baseHeader = (
        rootSurvey?.header ||
        QRDataObj[mapKey(activeItem)]?.header ||
        'survey_response'
      ).replace(/[^a-z0-9]/gi, '_');
      const fileName = multiSegment
        ? `${baseHeader}_${rootId}_with_addenda.pdf`
        : `${baseHeader}_${activeItem}.pdf`;

      const buildPdfCategoriesFromQuestionnaireResource = (
        resource: { item?: (LooseQuestionnaireItem | null)[] | undefined } | undefined,
      ): FlattenedCategory[] => {
        const result: FlattenedCategory[] = [];
        if (!resource?.item?.length) return result;
        // `category` is asserted rather than optional-chained: the array element
        // type admits `null`, and `category.linkId` throws on one today. A `?.`
        // here would turn that throw into a blank PDF section.
        resource.item.forEach((category) => {
          const catTranslation = getTranslation(category!);
          const categoryData: FlattenedCategory = {
            linkId: category!.linkId,
            text: (catTranslation ? catTranslation.toString() : category!.text) ?? category!.linkId,
            item: [],
          };
          flattenQuestionnaireItems(category!.item).forEach((question) => {
            const qTranslation = getTranslation(question);
            categoryData.item.push({
              linkId: question.linkId,
              text: (qTranslation ? qTranslation : question.text) ?? question.linkId,
              answerOptions: question.answerOption,
            });
          });
          result.push(categoryData);
        });
        return result;
      };

      const resolvePractitionerNameForPdf = (practitionerId: string): string | null => {
        if (!practitionerId || !props.practitionerData) return null;
        const entry = props.practitionerData.find((p) => p.resource.id === practitionerId);
        if (!entry) return null;
        const name = entry.resource.name?.[0];
        return (
          name?.text ?? (`${name?.given?.join(' ') ?? ''} ${name?.family ?? ''}`.trim() || null)
        );
      };

      const getAddendumSignaturePdfLine = (addendumRes: FhirQuestionnaireResponse): string => {
        const items = addendumRes.item;
        const authored = addendumRes.authored;
        const authorId = (() => {
          for (const group of items ?? []) {
            if (group.linkId === ADDENDUM_FIELD_IDS.metadata && group.item) {
              for (const item of group.item) {
                if (item.linkId === ADDENDUM_FIELD_IDS.author && item.answer?.[0]?.valueString) {
                  return item.answer[0].valueString;
                }
              }
            }
          }
          return null;
        })();
        const addendumAuthor =
          (authorId ? resolvePractitionerNameForPdf(authorId) : null) || 'Unknown Author';
        const credentials = findQuestionnaireResponseAnswerByLinkId(
          items,
          RD_SIGNATURE_CREDENTIALS_LINK_ID,
        );
        const signerWithCredentials = credentials
          ? `${addendumAuthor}, ${credentials}`
          : addendumAuthor;
        let cosignPractitionerId: string | null = null;
        for (const group of items ?? []) {
          if (ALL_COSIGN_GROUP_IDS.includes(group.linkId) && group.item) {
            for (const item of group.item) {
              if (
                ALL_COSIGN_PRACTITIONER_IDS.includes(item.linkId) &&
                item.answer?.[0]?.valueString
              ) {
                cosignPractitionerId = item.answer[0].valueString;
                break;
              }
            }
          }
          if (cosignPractitionerId) break;
        }
        const cosignerName = cosignPractitionerId
          ? resolvePractitionerNameForPdf(cosignPractitionerId)
          : null;
        const awaiting = isAwaitingCosign(items);
        const isDraft = addendumRes.status === 'amended';
        const dateStr = `${moment(authored).local().format('MM/DD/YYYY')} at ${moment(authored).local().format('h:mm A z')}`;

        if (isDraft && awaiting) {
          return `Electronically signed by ${signerWithCredentials} — pending co-sign${
            cosignerName ? ` assigned to ${cosignerName}` : ''
          }`;
        }
        if (isDraft && !awaiting) {
          return `Draft saved by ${signerWithCredentials} on ${dateStr}`;
        }
        if (cosignerName) {
          return `Electronically signed by ${signerWithCredentials} and co-signed by ${cosignerName} on ${dateStr}`;
        }
        return `Electronically signed and completed by ${signerWithCredentials} on ${dateStr}`;
      };

      /** Electronic-signature summary for RD consult chart (author from identifier / survey metadata, not addendum fields). */
      const getRdConsultChartSignaturePdfLine = (
        chartRes: FhirQuestionnaireResponse,
        surveyUserName: string,
      ): string => {
        const items = chartRes.item;
        const authored = chartRes.authored;
        const authorName =
          getLastModifiedBy(chartRes.identifier)?.trim() ||
          surveyUserName?.trim() ||
          'Unknown Author';
        const chartCredentials = findQuestionnaireResponseAnswerByLinkId(
          items,
          RD_SIGNATURE_CREDENTIALS_LINK_ID,
        );
        const chartSignerWithCredentials = chartCredentials
          ? `${authorName}, ${chartCredentials}`
          : authorName;
        const cosignPractitionerId = getCosignPractitionerId(items ?? []);
        const cosignerName = cosignPractitionerId
          ? resolvePractitionerNameForPdf(cosignPractitionerId)
          : null;
        const awaiting = isAwaitingCosign(items ?? []);
        const isDraft = chartRes.status === 'amended';
        const dateStr = `${moment(authored).local().format('MM/DD/YYYY')} at ${moment(authored).local().format('h:mm A z')}`;

        if (isDraft && awaiting) {
          return `Electronically signed by ${chartSignerWithCredentials} — pending co-sign${
            cosignerName ? ` assigned to ${cosignerName}` : ''
          }`;
        }
        if (isDraft && !awaiting) {
          return `Draft saved by ${chartSignerWithCredentials} on ${dateStr}`;
        }
        if (cosignerName) {
          return `Electronically signed by ${chartSignerWithCredentials} and co-signed by ${cosignerName} on ${dateStr}`;
        }
        return `Electronically signed and completed by ${chartSignerWithCredentials} on ${dateStr}`;
      };

      const pdf = new jsPDF('p', 'mm', 'a4');
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();

      const left = 14;
      const right = 14;
      const top = 14;
      const bottom = 14;
      const contentWidth = pageWidth - left - right;

      let y = top;

      const ensurePageSpace = (neededHeight = 8) => {
        if (y + neededHeight > pageHeight - bottom) {
          pdf.addPage();
          y = top;
        }
      };

      const writeText = (
        text: string,
        options?: {
          fontSize?: number;
          fontStyle?: 'normal' | 'bold';
          indent?: number;
          lineGap?: number;
          color?: [number, number, number];
        },
      ) => {
        const fontSize = options?.fontSize ?? 11;
        const fontStyle = options?.fontStyle ?? 'normal';
        const indent = options?.indent ?? 0;
        const lineGap = options?.lineGap ?? 5;
        const color = options?.color ?? [0, 0, 0];

        pdf.setFont('helvetica', fontStyle);
        pdf.setFontSize(fontSize);
        pdf.setTextColor(color[0], color[1], color[2]);

        const safeText = (text ?? '').toString().replace(/\s+/g, ' ').trim();
        const lines = pdf.splitTextToSize(safeText || ' ', contentWidth - indent) as string[];

        lines.forEach((line: string) => {
          ensurePageSpace(lineGap);
          pdf.text(line, left + indent, y);
          y += lineGap;
        });
      };

      const writeRightText = (
        text: string,
        yPos: number,
        options?: {
          fontSize?: number;
          fontStyle?: 'normal' | 'bold';
          color?: [number, number, number];
        },
      ) => {
        const fontSize = options?.fontSize ?? 10;
        const fontStyle = options?.fontStyle ?? 'bold';
        const color = options?.color ?? [70, 70, 70];

        pdf.setFont('helvetica', fontStyle);
        pdf.setFontSize(fontSize);
        pdf.setTextColor(color[0], color[1], color[2]);

        const safeText = (text ?? '').toString().replace(/\s+/g, ' ').trim();
        const textWidth = pdf.getTextWidth(safeText);
        pdf.text(safeText, pageWidth - right - textWidth, yPos);
      };

      const drawDivider = () => {
        ensurePageSpace(4);
        pdf.setDrawColor(190, 190, 190);
        pdf.line(left, y, pageWidth - right, y);
        y += 4;
      };

      const writeGap = (gap = 4) => {
        y += gap;
        ensurePageSpace();
      };

      /** Renders addendum signature line: full statement bold red when it is an electronic-signing line; otherwise gray. */
      const writeAddendumSignatureLine = (line: string) => {
        const safeLine = (line ?? '').toString().replace(/\s+/g, ' ').trim();
        if (!safeLine) return;

        const isElectronicSignStatement = /electronically signed/i.test(safeLine);
        writeText(safeLine, {
          fontSize: 9,
          fontStyle: isElectronicSignStatement ? 'bold' : 'normal',
          color: isElectronicSignStatement ? [200, 0, 0] : [85, 85, 85],
          lineGap: 4,
        });
      };

      const getAnswerText = (ans: QuestionnaireResponseItemAnswer): string => {
        if (ans.valueString) return ans.valueString;
        if (ans.valueDate) return ans.valueDate;
        if (ans.valueDecimal !== undefined) return String(ans.valueDecimal);
        if (ans.valueInteger !== undefined) return String(ans.valueInteger);
        if (ans.valueCoding?.display) return ans.valueCoding.display;
        if (ans.valueBoolean !== undefined) return ans.valueBoolean ? 'Yes' : 'No';
        return 'Attachment included';
      };

      const writePdfAnswerValue = (answer: QuestionnaireResponseItemAnswer) => {
        const att = answer?.valueAttachment;
        if (
          att?.data &&
          typeof att.data === 'string' &&
          typeof att.contentType === 'string' &&
          att.contentType.startsWith('image/')
        ) {
          try {
            const fmt = att.contentType.toLowerCase().includes('png') ? 'PNG' : 'JPEG';
            const dataUrl = att.data.startsWith('data:')
              ? att.data
              : `data:${att.contentType};base64,${att.data}`;
            const maxW = contentWidth - 18;
            // jsPDF 4.2.1 declares `getImageProperties`, so the `?.` guards a
            // method that is always present. Kept: it also swallows nothing,
            // and removing it would be a change to the call.
            const props = pdf.getImageProperties?.(dataUrl);
            const imgH = props?.width ? (props.height * maxW) / props.width : 45;
            ensurePageSpace(imgH + 8);
            pdf.addImage(dataUrl, fmt, left + 9, y, maxW, imgH);
            y += imgH + 4;
          } catch {
            writeText('• Signature or image captured (not embedded in PDF; see clinical record)', {
              fontSize: 10,
              indent: 9,
              color: [0, 115, 55],
              lineGap: 4,
            });
          }
          return;
        }

        writeText(`• ${getAnswerText(answer)}`, {
          fontSize: 10,
          indent: 9,
          color: [0, 115, 55],
        });
        writeGap(1);
      };

      let addendumOrdinal = 0;

      for (let segmentIndex = 0; segmentIndex < chainEntries.length; segmentIndex++) {
        const entry = chainEntries[segmentIndex]!;
        const qrId = getRes(entry).id;
        // `chainEntries` was filtered above on `QRDataObj[id]` being present, so
        // every id in this loop indexes a row.
        const survey = QRDataObj[mapKey(qrId)]!;

        if (segmentIndex > 0) {
          pdf.addPage();
          y = top;
        }

        let questionnaireCategories: FlattenedCategory[] =
          questionnaires?.[survey.questionnaire] || [];
        if (isAddendumQuestionnaire(survey.questionnaire)) {
          const qrEntry = findResponseData(qrId);
          const rawQ = props.questionnaireData?.entry?.find(
            (e) => e.resource.id === survey.questionnaire,
          )?.resource;
          if (qrEntry?.resource && rawQ) {
            const mergedQ = addendum.prepareEditQuestionnaire(
              qrEntry.resource,
              rawQ,
              props.questionnaireData,
            );
            questionnaireCategories = buildPdfCategoriesFromQuestionnaireResource(mergedQ);
          }
        }

        if (segmentIndex === 0) {
          pdf.setFont('helvetica', 'bold');
          pdf.setFontSize(18);
          pdf.setTextColor(0, 70, 140);
          const title = survey.header || 'Survey Response';
          const titleWidth = pdf.getTextWidth(title);
          pdf.text(title, (pageWidth - titleWidth) / 2, y);
          y += 8;
          const headerQr = findResponseData(qrId)?.resource;
          if (headerQr && isAddendumQuestionnaire(survey.questionnaire)) {
            if (chainEntries.length === 1) {
              writeAddendumSignatureLine(getAddendumSignaturePdfLine(headerQr));
              writeGap(2);
            }
          } else if (headerQr && isRdConsultChart(survey.questionnaire)) {
            writeAddendumSignatureLine(
              getRdConsultChartSignaturePdfLine(headerQr, survey.userName || ''),
            );
            writeGap(2);
          }
        } else {
          addendumOrdinal += 1;
          pdf.setFont('helvetica', 'bold');
          pdf.setFontSize(16);
          pdf.setTextColor(0, 70, 140);
          const sectionTitle = `Addendum ${addendumOrdinal}`;
          const sectionW = pdf.getTextWidth(sectionTitle);
          pdf.text(sectionTitle, (pageWidth - sectionW) / 2, y);
          y += 7;
          pdf.setFontSize(13);
          const sub = survey.header || 'Survey Response';
          const subW = pdf.getTextWidth(sub);
          pdf.text(sub, (pageWidth - subW) / 2, y);
          y += 8;

          const qrRes = findResponseData(qrId)?.resource;
          if (qrRes && isAddendumQuestionnaire(survey.questionnaire)) {
            writeAddendumSignatureLine(getAddendumSignaturePdfLine(qrRes));
            writeGap(2);
          }
        }

        if (survey.authoredOn) {
          writeRightText(`Date: ${survey.authoredOn}`, y, { fontSize: 10, color: [0, 70, 140] });
          y += 5;
        }
        if (survey.status) {
          writeRightText(`Status: ${survey.status}`, y, { fontSize: 10, color: [0, 70, 140] });
          y += 5;
        }
        if (survey.userName) {
          writeRightText(`Updated By: ${survey.userName}`, y, {
            fontSize: 10,
            color: [0, 70, 140],
          });
          y += 5;
        }

        writeGap(2);
        drawDivider();
        writeGap(2);

        const surveyAnswerByLinkId = indexQuestionnaireResponseAnswers(survey.questions);

        for (const category of questionnaireCategories) {
          if (category?.linkId && SURVEYS_PDF_EXCLUDED_LINK_IDS.has(category.linkId)) continue;

          const questionsForPdf = flattenQuestionnaireItems(category.item).filter(
            (q) => !SURVEYS_PDF_EXCLUDED_LINK_IDS.has(q?.linkId as string),
          );
          if (questionsForPdf.length === 0) continue;

          writeText(category.text || category.linkId || 'Section', {
            fontSize: 11,
            fontStyle: 'bold',
            color: [0, 90, 180],
            indent: 0,
            lineGap: 6,
          });

          writeGap(2);

          for (const question of questionsForPdf) {
            const answers = surveyAnswerByLinkId.get(mapKey(question.linkId)) ?? [];

            writeText(stripHtml(question.text || question.linkId || 'Question'), {
              fontSize: 11,
              indent: 3,
              color: [35, 35, 35],
            });

            if (!answers.length) {
              writeText('No answer provided', {
                fontSize: 10,
                indent: 9,
                color: [140, 140, 140],
              });
              writeGap(2);
              continue;
            }

            for (const answer of answers) {
              writePdfAnswerValue(answer);
            }

            writeGap(2);
          }

          writeGap(3);
        }
      }

      pdf.save(fileName);

      for (const entry of chainEntries) {
        const qrId = getRes(entry).id;
        const qr = findResponseData(qrId);
        if (qr?.resource) {
          logDownload(
            qrId!,
            qr.resource,
            ` questionnaire response has been downloaded as part of combined PDF (${fileName}).`,
          );
        }
      }
    } catch (e) {
      console.error(e);
      // A `catch` binding is `unknown`. Spelled through a shape that states only
      // the two members this line reads, rather than through `any`. `toString`
      // is required on the shape because the `?.` on the value — not on the
      // call — is what the original wrote, and `Object.prototype.toString` is
      // always present on anything that is not nullish.
      const err = e as { message?: string; toString: () => string } | undefined;
      setError(`Failed to download PDF: ${err?.message || err?.toString()}`);
    }
  };

  const logDownload = (QRId: string, payload: FhirQuestionnaireResponse, message: string) => {
    const name = findQuestionnaireTitle(payload);
    const proxyURL = import.meta.env.VITE_API_ENDPOINT + '/fhir/';
    const fhirProxyClient = new FHIRProxyClient(authInfo.token, proxyURL);

    const provenance: Provenance = ProvenanceBuilder.build(
      `QuestionnaireResponse/${QRId}`,
      {
        reference: 'Practitioner/' + authInfo.practitionerId,
        display: authInfo.practitionerName,
      },
      props.patientId,
      'Survey Download',
      `${name}${message}`,
      { code: props.org.toUpperCase(), display: props.org.toLowerCase() },
    );
    console.log('Logging provenance for tasks notes download: ', JSON.stringify(provenance));
    const fhirBundle = ProvenanceBuilder.buildProvenanceBundle([payload], provenance);

    fhirProxyClient.send('post', '', fhirBundle).then(
      (response) => {
        console.log('Download provenance logged', response);
      },
      (response) => {
        console.log('Failed to log download provenance', response);
      },
    );
  };

  const logDelete = (QRId: string, payload: FhirQuestionnaireResponse, message: string) => {
    const name = findQuestionnaireTitle(payload);
    const proxyURL = import.meta.env.VITE_API_ENDPOINT + '/fhir/';
    const fhirProxyClient = new FHIRProxyClient(authInfo.token, proxyURL);
    const provenance: Provenance = ProvenanceBuilder.build(
      `QuestionnaireResponse/${QRId}`,
      {
        reference: 'Practitioner/' + authInfo.practitionerId,
        display: authInfo.practitionerName,
      },
      props.patientId,
      'DELETE',
      `${name}${message}`,
      { code: props.org.toUpperCase(), display: props.org.toLowerCase() },
    );
    const fhirBundle = ProvenanceBuilder.buildProvenanceBundle([payload], provenance);
    fhirProxyClient.send('post', '', fhirBundle).then(
      (response) => {
        console.log(response);
        setTimeout(() => {
          setLoader(false);
        }, 4000);
      },
      (response) => {
        console.log(response);
      },
    );
  };

  /**
   * `qrId` admits `null`/`undefined` because `activeItem` — the argument at
   * every call site but one — carries both. The body's `==` comparison is loose,
   * so a nullish `qrId` matches a response whose `id` is absent; that is the
   * behaviour today and is left as written.
   */
  const findResponseData = (qrId: string | null | undefined) => {
    return QuestionnaireResponseData?.entry?.filter(
      (qr: ResourceEntry<'QuestionnaireResponse'>) => {
        if (qr?.resource?.id == qrId) return qr.resource;
      },
    )[0];
  };

  const findQuestionnaireTitle = (qr: FhirQuestionnaireResponse | undefined) => {
    const QRHelper = new QuestionnaireResponseHelper(qr);
    const questionnaireId = QRHelper.getQuestionnaireID();
    let questionnaireTitle = '';

    if (props.questionnaireData?.entry) {
      for (let i = 0; i < props.questionnaireData.entry.length; i++) {
        const QHelper = new QuestionnaireHelper(props.questionnaireData.entry[i]!.resource);
        if (questionnaireId === QHelper.getId()) {
          questionnaireTitle = QHelper.getTitle()!;
          break;
        }
      }
    }

    return questionnaireTitle;
  };

  const deleteSurvey = async (id: string) => {
    const qr = findResponseData(id);
    setDeleteConfirm(false);
    setLoader(true);

    if (qr) {
      const qId = qr.resource.questionnaire?.substring(14);
      const isChart = isRdConsultChart(qId!);

      const linkedAddenda = isChart
        ? (QuestionnaireResponseData?.entry?.filter((entry) => {
            const ogId = getOriginalChartId(entry.resource.extension);
            const parentId = getParentResponseId(entry.resource.extension);
            const isLinked = ogId === id || parentId === id;
            return isLinked && entry.resource.status !== 'entered-in-error';
          }) ?? [])
        : [];

      for (const addendumEntry of linkedAddenda) {
        const addendumPayload = { ...addendumEntry.resource };
        addendumPayload['identifier'] = buildIdentifier(authInfo.practitionerName);
        addendumPayload['status'] = 'entered-in-error';
        logDelete(
          addendumEntry.resource.id!,
          addendumPayload,
          ' linked addendum is being deleted...',
        );
        try {
          await FHIRUpdater(
            '/QuestionnaireResponse/' + addendumEntry.resource.id,
            authInfo.token,
            addendumPayload,
          );
          logDelete(
            addendumEntry.resource.id!,
            addendumPayload,
            ' linked addendum has been successfully deleted.',
          );
        } catch {
          logDelete(
            addendumEntry.resource.id!,
            addendumPayload,
            ' linked addendum failed to delete.',
          );
        }
      }

      const payload = qr.resource;
      payload['identifier'] = buildIdentifier(authInfo.practitionerName);
      payload['status'] = 'entered-in-error';
      logDelete(id, payload, ' questionnaire is being deleted...');

      // The `/fhir` proxy answers a failed update with its own error envelope
      // rather than the persisted resource; `FHIRUpdater`'s default
      // (`fhir4.Resource`) is the SUCCESS shape only, and this reader tests for
      // the failure shape first. Named as an intersection, not a union, so the
      // existing property read stays as written — a union would force an `in`
      // narrowing, which is a runtime change.
      FHIRUpdater<fhir4.Resource & { errorMessage?: string }>(
        '/QuestionnaireResponse/' + id,
        authInfo.token,
        payload,
      ).then(
        (response) => {
          if (response.errorMessage) {
            setError('Failed to delete survey response: ' + response.errorMessage);
            logDelete(id, payload, ' questionnaire response has been failed to delete.');
          } else {
            setError('');
            setActiveItem('');
            refreshQA(true);
            logDelete(id, payload, ' questionnaire response has been successfully deleted.');
          }
        },
        (error) => {
          setError('Failed to delete survey response: ' + (error as Error).toString());
          logDelete(id, payload, ' questionnaire response has been failed to delete.');
        },
      );
    }

    setTimeout(() => {
      setLoader(false);
    }, 4000);
  };

  const refreshQA = (reset?: boolean) => {
    // eslint-disable-next-line @typescript-eslint/no-floating-promises -- fire-and-forget revalidation; a rejection only leaves the view stale
    props.questionnaireResponseMutate().then((data) => {
      if (data && data.entry && reset && reset !== undefined) {
        // `entry[0]` and its `resource` are both asserted: `Bundle.entry` is
        // `0..*` and `BundleEntry.resource` `0..1`, and this reads the first
        // entry after testing only that `entry` exists. A present-but-empty
        // `entry` array throws here today.
        setActiveItem(data.entry[0]!.resource!.id);
        setCurrentQuestionnaire(null);
        props?.setQRID(undefined);
      }
      // The state holds the narrow `{ resource }`-required shape this file reads with, so the
      // entry list is CAST rather than filtered. `bundleEntriesOf` was used here and called
      // `Array.prototype.filter`, dropping entries whose `resource` is absent; upstream stores
      // the bundle as it arrived (`develop:src/components/patient/questionnaires/responses.tsx:815`
      // @ `f46fc9519` is a bare `setQuestionnaireResponseData(data)`). The cast emits nothing.
      //
      // The surrounding object rebuild IS compelled: the state type is narrower than `Bundle`.
      // An ABSENT `entry` stays absent rather than becoming `[]` — the render below distinguishes
      // the two, showing "No Surveys Found!!!" only for the absent case.
      setQuestionnaireResponseData(
        data
          ? { ...(data.entry ? { entry: data.entry as QuestionnaireResponseEntry[] } : {}) }
          : undefined,
      );
    });
  };

  const refreshQAForDraftSurveys = async (
    activeItem?: string,
    currentQuestionnaire?: FhirQuestionnaire | null,
    QRID?: string | null,
  ) => {
    await props.questionnaireResponseMutate().then((data) => {
      if (data && data.entry) {
        // `entry[0]`/`resource` asserted for the same reason as in `refreshQA`:
        // a present-but-empty `entry` throws here today.
        setActiveItem(activeItem ?? data.entry[0]!.resource!.id);
        setCurrentQuestionnaire(currentQuestionnaire ?? null);
        props?.setQRID(QRID ?? undefined);
      }
    });
  };

  /**
   * The `/Questionnaire` search entries, assigned from `props.questionnaireData.entry`
   * in the render body below.
   *
   * Declared without `| undefined` because that prop's `entry` is optional and
   * this is read unguarded by `handleTranslation` — the declaration records the
   * value the reads assume, not the one the prop can supply. See the assertion
   * at the assignment.
   */
  let questionsList: QuestionnaireEntry[];

  const handleTranslation = (questionnaireId: string | undefined) => {
    let hasTranslation = false;
    const survey = questionsList.filter((resources) => {
      if (resources.resource.id === questionnaireId) return resources.resource;
    });

    if (survey && survey[0]) {
      const qh = new QuestionnaireHelper(survey?.[0]?.resource);
      for (let i = 0; i < qh.getItemListLength()!; i++) {
        // `getGroupLength` returns `number | undefined` and `j < undefined` is
        // false, so an item with no nested `item` skips its inner loop — the
        // behaviour `Helpers.spec.ts` pins.
        for (let j = 0; j < qh.getGroupLength(i)!; j++) {
          const translationHelper = new QuestionnaireExtensionHelper(qh.getQuestionExtension(i, j));
          if (translationHelper.hasTranslation()) {
            hasTranslation = true;
          }
        }
      }
    }

    return hasTranslation;
  };

  const createDataObj = (
    QRItem: ResourceEntry<'QuestionnaireResponse'>,
    questionnaireTitle: string,
    questionnaireId: string | undefined,
  ): SurveyListRow => {
    let translationExists = false;
    translationExists = handleTranslation(questionnaireId);

    return {
      authoredOn:
        QRItem.resource.authored &&
        moment(QRItem.resource.authored, 'YYYY-MM-DD').format('MMMM Do, YYYY'),
      // SUSPECTED DEFECT — REPORTED, NOT FIXED. `QuestionnaireResponse.questionnaire`
      // is `0..1` in FHIR R4 and `.substring` throws on absence, so one response
      // saved without a questionnaire canonical fails the whole survey list rather
      // than that row. The fixed offset of 14 also assumes a specific canonical
      // prefix; a differently-shaped url silently yields the wrong id.
      questionnaire: QRItem.resource.questionnaire!.substring(
        14,
        QRItem.resource.questionnaire!.length,
      ),
      userName: getLastModifiedBy(QRItem.resource.identifier),
      questions: QRItem.resource.item,
      header: questionnaireTitle,
      hasTranslation: translationExists,
      status: QRItem.resource.status,
    };
  };

  const addAddendum = () => {
    if (!activeItem) return;
    const result = addendum.prepareAddendum(
      activeItem,
      props.questionnaireData,
      QuestionnaireResponseData,
      findResponseData,
    );
    if (!result) {
      setError('Addendum questionnaire not found.');
      return;
    }
    setCurrentQuestionnaire(result.questionnaire);
    setActiveItem('');
    setCurrentQuestionnaireResponse(result.prefillResponse ?? undefined);
  };

  const duplicateSurvey = () => {
    // The `&&` pairs here guard the ELEMENT (`[0]`) but never the array: both
    // `entry` props are declared optional, so a bundle with no `entry` throws on
    // `.filter`. `qResponse` is likewise dereferenced unguarded on the next
    // statement — when no response matches `activeItem` the `&&` yields
    // undefined and `qResponse.questionnaire` throws. Preserved as written.
    const qResponse =
      props.questionnaireResponseData.entry!.filter(
        (response) => response.resource.id === activeItem,
      )[0] &&
      props.questionnaireResponseData.entry!.filter(
        (response) => response.resource.id === activeItem,
      )[0]!.resource;

    const qrQuestionniare =
      props.questionnaireData.entry!.filter(
        (questionnaire) => questionnaire.resource.id === qResponse!.questionnaire!.substring(14),
      )[0] &&
      props.questionnaireData.entry!.filter(
        (questionnaire) => questionnaire.resource.id === qResponse!.questionnaire!.substring(14),
      )[0]!.resource;

    setCurrentQuestionnaire(qrQuestionniare);
    setActiveItem(null);

    // Annotated so `resourceType` and `status` infer as FHIR's literal unions
    // rather than widening to `string`. `id: ''` is the sentinel the save path
    // reads as "not yet persisted".
    const newResponse: Partial<FhirQuestionnaireResponse> = {
      id: '',
      resourceType: 'QuestionnaireResponse',
      questionnaire: `Questionnaire/${qrQuestionniare!.id}`,
      identifier: buildIdentifier(authInfo.practitionerName),
      status: 'in-progress',
      authored: new Date().toISOString(),
      subject: {
        reference: `Patient/${props.patientId}`,
      },
      extension: [
        {
          url: 'https://projectwell.io/fhir/extensions/completed-date',
          valueDateTime: new Date().toISOString(),
        },
        {
          url: 'https://projectwell.io/fhir/extensions/updated-date',
          valueDateTime: new Date().toISOString(),
        },
      ],
      item: qResponse!.item,
    };
    setCurrentQuestionnaireResponse(newResponse);
  };

  /**
   * Per-linkId question titles and answer options.
   *
   * SUSPECTED DEFECT — REPORTED, NOT FIXED. This map is WRITE-ONLY. It is
   * rebuilt on every render by the loop below — walking every questionnaire,
   * every page and every flattened question — and nothing ever reads a value
   * back out of it: the only reads are the two `hasOwnProperty` probes inside
   * the loop that populates it. The render work is therefore wasted, and the
   * `title`/`answers` it computes are dead. Typed as written rather than
   * removed, since deleting a loop is a behaviour change.
   */
  const questions: Record<
    string,
    | { title?: string | undefined; answers?: QuestionnaireItemAnswerOption[] | undefined }
    | undefined
  > = {};

  if (!QuestionnaireResponseData) {
    return <Loader active inline="centered" />;
  } else if (QuestionnaireResponseData) {
    const QRData = QuestionnaireResponseData.entry;
    // `entry` is `0..*` on the prop, and the two `questionsList.forEach` walks
    // below are unguarded — a `/Questionnaire` search that matched nothing
    // throws here today.
    questionsList = props.questionnaireData.entry!;

    // Kept as an expression statement: an `if` would emit different JavaScript.
    // eslint-disable-next-line @typescript-eslint/no-unused-expressions
    QRData &&
      QRData.forEach((QRItem) => {
        const QRHelper = new QuestionnaireResponseHelper(QRItem.resource ?? []);
        const questionnaireId = QRHelper.getQuestionnaireID();
        let questionnaireTitle = '';

        if (props.questionnaireData?.entry) {
          for (let i = 0; i < props.questionnaireData.entry.length; i++) {
            // In range by the loop bound.
            const questionnaireHelper = new QuestionnaireHelper(
              props.questionnaireData.entry[i]!.resource,
            );
            const QExtHelper = new QuestionnaireExtensionHelper(
              questionnaireHelper.getSurveyExtension(),
            );
            if (questionnaireId === questionnaireHelper.getId()) {
              // `getTitle()` is optional, and the assignment target is a
              // `string`. An untitled questionnaire really does put undefined
              // here today; the assertion keeps it rather than substituting ''.
              questionnaireTitle = QExtHelper.getInternalTitle() ?? questionnaireHelper.getTitle()!;
              break;
            }
          }
        }

        // `getId()` is optional; `mapKey` names the coercion the subscript
        // already performed, so an id-less response keeps landing on the literal
        // key "undefined", collapsing all such responses onto one slot.
        QRDataObj[mapKey(QRHelper.getId())] = createDataObj(
          QRItem,
          questionnaireTitle,
          questionnaireId,
        );
      });

    questionsList.forEach((resources) => {
      const resource = resources.resource;
      // `id` is optional on every FHIR resource, and `linkId` on a questionnaire
      // item. `mapKey` names the coercion the subscript already performed, so an
      // id-less resource keeps landing on the literal key "undefined" — the same
      // collapsing this file documents for `getId()` above.
      const resourceKey = mapKey(resource.id);
      questions[resourceKey] = {};
      questions[resourceKey].title = resource.title;

      // Kept as an expression statement: an `if` would emit different JavaScript.
      // eslint-disable-next-line @typescript-eslint/no-unused-expressions
      resource.item &&
        resource.item.length &&
        resource.item.forEach((category: LooseQuestionnaireItem) => {
          const categoryKey = mapKey(category.linkId);
          if (Object.prototype.hasOwnProperty.call(questions, categoryKey)) {
            questions[categoryKey]!.title = category.text;
          } else {
            questions[categoryKey] = {};
            questions[categoryKey].title = category.text;
          }

          // `linkId` is optional on a QuestionnaireItem. An item without one is
          // keyed under the string "undefined" here, exactly as before.
          flattenQuestionnaireItems(category.item).forEach((question) => {
            const questionKey = mapKey(question.linkId);
            if (Object.prototype.hasOwnProperty.call(questions, questionKey)) {
              const translation: string | undefined = getTranslation(question);
              questions[questionKey]!.title = translation ? translation : question.text;
              questions[questionKey]!.answers = question.answerOption;
            } else {
              questions[questionKey] = {};
              questions[questionKey].title = question.text;
              questions[questionKey].answers = question.answerOption;
            }
          });
        });
    });

    questionsList.forEach((questionnaire) => {
      const resource = questionnaire.resource;
      const resourceKey = mapKey(resource.id);
      questionnaires[resourceKey] = [];

      // Kept as an expression statement: an `if` would emit different JavaScript.
      // eslint-disable-next-line @typescript-eslint/no-unused-expressions
      resource.item &&
        resource.item.length &&
        resource.item.forEach((category: LooseQuestionnaireItem) => {
          const translation: string | undefined = getTranslation(category);
          const categoryData: FlattenedCategory = {
            linkId: category.linkId,
            text: translation ? translation.toString() : category.text,
            item: [],
          };

          flattenQuestionnaireItems(category.item).forEach((question) => {
            const translation: string | undefined = getTranslation(question);
            const questionData = {
              linkId: question.linkId,
              text: translation ? translation : question.text,
              answerOptions: question.answerOption,
            };
            categoryData.item.push(questionData);
          });

          questionnaires[resourceKey]!.push(categoryData);
        });
    });

    const activeQDef = props.questionnaireData?.entry?.find(
      (q) => q.resource.id === QRDataObj?.[mapKey(activeItem)]?.questionnaire,
    )?.resource;
    const lockedReadOnly =
      QRDataObj?.[mapKey(activeItem)]?.status === 'completed' &&
      isReadOnlyAfterCompletion(activeQDef?.extension);

    const { canShowAddAddendum, pendingAddendumId } = (() => {
      if (!lockedReadOnly)
        return { canShowAddAddendum: false, pendingAddendumId: null as string | null };
      const activeRes = findResponseData(activeItem)?.resource;
      if (!activeRes)
        return { canShowAddAddendum: false, pendingAddendumId: null as string | null };

      const ogChartId = getOriginalChartId(activeRes.extension);
      const resolvedOgChartId = ogChartId ?? activeItem;

      const allAddenda = QuestionnaireResponseData?.entry?.filter((qr) => {
        const qrOgId = getOriginalChartId(qr.resource.extension);
        return qrOgId === resolvedOgChartId && qr.resource.status !== 'entered-in-error';
      });

      const draftAddendum = allAddenda?.find((qr) => {
        const res = qr.resource;
        if (res.status !== 'amended') return false;
        const qId = res.questionnaire?.substring(14);
        return (
          requiresCosignWorkflow(qId!) &&
          isAwaitingCosign(res.item) &&
          isSubmittedForCosign(res.extension)
        );
      });

      if (draftAddendum) {
        return {
          canShowAddAddendum: false,
          pendingAddendumId: draftAddendum.resource.id as string,
        };
      }

      const completedAddenda = allAddenda
        ?.filter((qr) => qr.resource.status === 'completed')
        ?.sort(
          (a, b) => moment(b.resource.authored).valueOf() - moment(a.resource.authored).valueOf(),
        );

      if (!ogChartId) {
        return { canShowAddAddendum: true, pendingAddendumId: null as string | null };
      }
      return {
        canShowAddAddendum: completedAddenda?.[0]?.resource?.id === activeItem,
        pendingAddendumId: null as string | null,
      };
    })();

    const { activeQrAwaitingCosign, isAssignedCosignerForActiveQr } = (() => {
      const activeRes = findResponseData(activeItem)?.resource;
      if (!activeRes || activeRes.status !== 'amended')
        return { activeQrAwaitingCosign: false, isAssignedCosignerForActiveQr: false };
      const qId = activeRes.questionnaire?.substring(14);
      if (!requiresCosignWorkflow(qId!))
        return { activeQrAwaitingCosign: false, isAssignedCosignerForActiveQr: false };
      const awaiting =
        isAwaitingCosign(activeRes.item) && isSubmittedForCosign(activeRes.extension);
      const assignedId = awaiting ? getCosignPractitionerId(activeRes.item) : null;
      return {
        activeQrAwaitingCosign: awaiting,
        isAssignedCosignerForActiveQr: awaiting && assignedId === authInfo.practitionerId,
      };
    })();

    const displayImage = (valueReference: Reference | undefined) => {
      const referenceId = valueReference?.reference?.split('/')?.[1];

      if (surveyImage[mapKey(referenceId)]?.contentType !== 'application/pdf') {
        return (
          <div>
            <img
              key={referenceId}
              id={referenceId}
              width="100%"
              src={surveyImage[mapKey(referenceId)]?.data}
              alt="survey attachment"
            />
          </div>
        );
      } else if (surveyImage[mapKey(referenceId)]?.contentType === 'application/pdf') {
        // The subscript can miss — the map is keyed by DocumentReference id and
        // this reference may not have been fetched — so `data` is
        // `string | undefined` while `base64ToPdfBlob` takes a `string`. The
        // branch is only entered when `contentType` matched on the same lookup,
        // so a miss cannot reach here; asserted rather than guarded.
        const blob = base64ToPdfBlob(surveyImage?.[mapKey(referenceId)]!.data);
        const url = URL.createObjectURL(blob);

        return (
          <div>
            <object key={referenceId}>
              <iframe
                title={'survey-pdf-' + referenceId}
                id={referenceId}
                width="100%"
                height="600"
                src={`${url}#toolbar=0&navpanes=0&scrollbar=0`}
              />
            </object>
          </div>
        );
      }
    };

    /**
     * `answers` is declared as the possibly-absent `DiffAnswer` because that is
     * what `QuestionDiffItem` — the consumer this is handed to — is typed to
     * pass. The body reads `answers![answers!.length - 1]!` unguarded, so `null`
     * and `[]` both throw; see the note on that prop and the block comment
     * below.
     *
     * `answerOptions` is the concrete FHIR option array. The consumer forwards
     * `QA.answerOptions`, which it declares `unknown` — a second disagreement at
     * the same boundary, asserted at the JSX site rather than widened here,
     * because the body iterates the options as FHIR values.
     */
    const displayAnswers = (
      answers: QuestionnaireResponseItemAnswer[] | null | undefined,
      answerOptions: QuestionnaireItemAnswerOption[] | undefined,
    ) => {
      let existingAnswer = false;

      return (
        <div>
          {answerOptions
            ? answerOptions.map((option) => {
                if (option.valueCoding) {
                  const translation: string | undefined = getTranslation(option.valueCoding);
                  let jsx = (
                    <div key={option.valueCoding.code}>
                      {translation ?? option.valueCoding.display}
                    </div>
                  );

                  for (let i = 0; i < answers!.length; i++) {
                    // `answers[i]` is in range by the loop bound. The inner
                    // `valueCoding` was just tested on the line above.
                    if (answers![i]!.valueCoding) {
                      if (option.valueCoding.code === answers![i]!.valueCoding!.code) {
                        existingAnswer = true;
                        jsx = (
                          <List.Header key={option.valueCoding.code}>
                            <span> {translation ?? option.valueCoding.display} </span>
                            <List.Icon name="check" />
                          </List.Header>
                        );
                        break;
                      }
                    }
                  }

                  return jsx;
                } else if (
                  (option.valueInteger && option.valueInteger >= 0) ||
                  option.valueInteger === 0
                ) {
                  const QA = option.valueInteger;
                  let jsx = <div key={QA}>{QA}</div>;

                  for (let i = 0; i < answers!.length; i++) {
                    // In range by the loop bound, as above.
                    if (
                      (answers![i]!.valueInteger && answers![i]!.valueInteger! >= 0) ||
                      answers![i]!.valueInteger === 0
                    ) {
                      if (option.valueInteger === answers![i]!.valueInteger) {
                        jsx = (
                          <List.Header key={QA}>
                            <span> {QA} </span>
                            <List.Icon name="check" />
                          </List.Header>
                        );
                        break;
                      }
                    }
                  }

                  return jsx;
                }

                return null;
              })
            : /* Every `answers[answers.length - 1]` below reads the last answer
                 with no emptiness test. `answers` is a plain required array, so
                 an item whose answer list is EMPTY indexes at -1, yields
                 undefined, and throws on the property read — taking the whole
                 response card down. The assertions record that; adding a guard
                 would render an empty card instead of throwing. */
              answers![answers!.length - 1]!.valueInteger && (
                <List.Header key={answers![answers!.length - 1]!.valueInteger}>
                  <span> {answers![answers!.length - 1]!.valueInteger} </span>
                  <List.Icon name="check" />
                </List.Header>
              )}

          {answers![answers!.length - 1]!.valueString && (
            <List.Header key={answers![answers!.length - 1]!.valueString}>
              <span style={{ whiteSpace: 'pre-wrap' }}>
                {' '}
                {answers![answers!.length - 1]!.valueString}{' '}
              </span>
              <List.Icon name="check" />
            </List.Header>
          )}

          {answers![answers!.length - 1]!.valueDate && (
            <List.Header key={answers![answers!.length - 1]!.valueDate}>
              <span style={{ whiteSpace: 'pre-wrap' }}>
                {answers![answers!.length - 1]!.valueDate}
              </span>
              <List.Icon name="check" />
            </List.Header>
          )}

          {answers![answers!.length - 1]!.valueDecimal && (
            <List.Header key={answers![answers!.length - 1]!.valueDecimal}>
              <span style={{ whiteSpace: 'pre-wrap' }}>
                {answers![answers!.length - 1]!.valueDecimal}
              </span>
              <List.Icon name="check" />
            </List.Header>
          )}

          {answers![answers!.length - 1]!.valueReference &&
            displayImage(answers![answers!.length - 1]!.valueReference)}

          {!existingAnswer &&
            answers!.map((answer, index) => {
              if (answer.valueCoding) {
                const translation: string | undefined = getTranslation(answer.valueCoding);
                return (
                  <List.Header key={index}>
                    <span>Retired Answer: {translation ?? answer.valueCoding.display} </span>
                    <List.Icon name="check" />
                  </List.Header>
                );
              }
              return null;
            })}
        </div>
      );
    };

    const filterResponses = (
      questionnaireTitle: string,
      state: { title: null | string; status: null | string },
      questionnaireStatus: string,
    ): boolean => {
      const filterResult: boolean =
        state?.title?.toLowerCase() === questionnaireTitle?.toLowerCase();
      const statusCheck: boolean = !state.status || questionnaireStatus === state.status;

      if ((!state.title || filterResult) && statusCheck) {
        return true;
      } else if (questionnaireTitle === '') {
        return false;
      }

      return false;
    };

    const displayQA = (
      responseItems: LooseQuestionnaireResponseItem[] | undefined,
      questionnaireCategory: LooseQuestionnaireItem | undefined,
      /**
       * Keyed by `string | undefined`, not `string`: the producer inserts with
       * `diffs.set(QA.linkId, …)` and `linkId` is `0..1`, so an `undefined` key
       * genuinely occupies this map and `get(undefined)` genuinely hits it.
       * `mapKey` would be wrong here for exactly that reason.
       */
      diffs?: Map<string | undefined, { oldAnswer: DiffAnswer }>,
      answerByLinkId?: Map<string, QuestionnaireResponseItemAnswer[]>,
    ) => {
      const answerIndex = answerByLinkId ?? indexQuestionnaireResponseAnswers(responseItems);

      return (
        <List.List>
          {/*
            SUSPECTED DEFECT — REPORTED, NOT FIXED. `LooseQuestionnaireItem['item']`
            is `(LooseQuestionnaireItem | null)[]` — the `null` is declared
            deliberately in `./fhir-helpers.ts`, so a null entry is a shape this
            code says it expects — but this map dereferences `QA.linkId` without
            testing for it, so one null item throws during render and the whole
            questionnaire panel goes blank.
          */}
          {questionnaireCategory?.item?.map((QA) => {
            const answer = answerIndex.get(mapKey(QA!.linkId)) ?? null;

            return (
              <QuestionDiffItem
                key={QA!.linkId}
                QA={QA!}
                answer={answer}
                diffInfo={diffs?.get(QA!.linkId)}
                // `QuestionDiffItem` declares the second parameter `unknown`
                // while this producer iterates it as `QuestionnaireItemAnswerOption[]`
                // — the same boundary disagreement noted on `displayAnswers`
                // itself. What is forwarded is `QA.answerOptions`, which for
                // these nodes is exactly that array.
                displayAnswers={
                  displayAnswers as (ans: DiffAnswer, opts: unknown) => React.ReactNode
                }
                tasksData={props?.tasksData}
              />
            );
          })}
        </List.List>
      );
    };

    const displayCategories = (
      categories: LooseQuestionnaireResponseItem[] | undefined,
      questionnaireCategories: LooseQuestionnaireItem[] | undefined,
    ) => {
      const answerByLinkId = indexQuestionnaireResponseAnswers(categories);

      return (
        <>
          {imgLoader && <Loader active inline="centered" content={'Loading...'} />}
          {!imgLoader && (
            <List.List>
              {questionnaireCategories &&
                questionnaireCategories.map((category: LooseQuestionnaireItem) => {
                  return (
                    <List.Item key={category.linkId}>
                      <List.Icon name="folder open outline" />
                      <List.Content>
                        <List.Header>{category.text ?? category.linkId}</List.Header>
                        <List.List>
                          {displayQA(categories, category, undefined, answerByLinkId)}
                        </List.List>
                      </List.Content>
                    </List.Item>
                  );
                })}
            </List.List>
          )}
        </>
      );
    };

    const displayChartCopy = (qrId: string) => {
      const qrEntry = findResponseData(qrId);
      if (!qrEntry) return null;

      const qId = qrEntry.resource.questionnaire?.substring(14);
      if (!isAddendumQuestionnaire(qId!)) return null;

      const chartQ = props.questionnaireData?.entry?.find((q) =>
        isRdConsultChart(q.resource.id!),
      )?.resource;
      if (!chartQ?.item) return null;

      const chartLinkIds = new Set(
        chartQ.item.filter((g) => !EXCLUDED_CHART_GROUPS.has(g.linkId)).map((g) => g.linkId),
      );
      const qrItems: LooseQuestionnaireResponseItem[] = qrEntry.resource.item ?? [];
      const chartResponseItems = qrItems.filter((item) => chartLinkIds.has(item.linkId!));
      if (chartResponseItems.length === 0) return null;

      const chartAnswerByLinkId = indexQuestionnaireResponseAnswers(chartResponseItems);

      const originalChartId = getOriginalChartId(qrEntry.resource.extension);
      const chainParentId = getChainParentId(qrEntry.resource.extension);

      const diffs = new Map<string | undefined, { oldAnswer: DiffAnswer }>();
      if (chainParentId) {
        const parentRes = findResponseData(chainParentId)?.resource;
        if (parentRes && parentRes.item) {
          chartQ.item
            .filter((g) => !EXCLUDED_CHART_GROUPS.has(g.linkId))
            .forEach((category) => {
              const currCat = chartResponseItems.find((c) => c.linkId === category.linkId);
              const parentCat = parentRes.item!.find((c) => c.linkId === category.linkId);

              flattenQuestionnaireItems(category.item).forEach((QA) => {
                const currAns = findQuestionnaireResponseAnswers(currCat?.item, QA.linkId!);
                const parentAns = findQuestionnaireResponseAnswers(parentCat?.item, QA.linkId!);

                const currStr = currAns ? JSON.stringify(currAns) : undefined;
                const parentStr = parentAns ? JSON.stringify(parentAns) : undefined;

                if (currStr !== parentStr) {
                  diffs.set(QA.linkId, { oldAnswer: parentAns });
                }
              });
            });
        }
      }

      const showParentLink = chainParentId && originalChartId && chainParentId !== originalChartId;

      return (
        <div style={{ marginTop: '20px', borderTop: '2px solid #2185d0', paddingTop: '10px' }}>
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '14px',
            }}
          >
            <Header as="h3" style={{ margin: 0 }}>
              <Icon name="file alternate" color="blue" />
              RD Consult Chart (Copy)
            </Header>
            {showParentLink && (
              <Button
                size="mini"
                color="teal"
                basic
                icon
                labelPosition="right"
                onClick={() => {
                  // eslint-disable-next-line @typescript-eslint/no-floating-promises -- fire-and-forget panel load; a rejection leaves the selected item's detail empty with no error shown
                  handleItemClick(chainParentId);
                  setCurrentQuestionnaire(null);
                }}
              >
                View Source Addendum
                <Icon name="arrow up" />
              </Button>
            )}
          </div>
          <List.List>
            {chartQ.item
              .filter((g) => !EXCLUDED_CHART_GROUPS.has(g.linkId))
              .map((category) => {
                const _responseCategory = chartResponseItems.find(
                  (item) => item.linkId === category.linkId,
                );
                const categoryTranslation = getTranslation(category);
                const categoryForDisplay = {
                  linkId: category.linkId,
                  text: categoryTranslation ? categoryTranslation.toString() : category.text,
                  item: flattenQuestionnaireItems(category.item).map((q) => {
                    const qTranslation = getTranslation(q);
                    return {
                      linkId: q.linkId,
                      text: qTranslation ? qTranslation : q.text,
                      answerOptions: q.answerOption,
                    };
                  }),
                };
                return (
                  <List.Item key={category.linkId}>
                    <List.Icon name="folder open outline" />
                    <List.Content>
                      <List.Header>{categoryForDisplay.text ?? category.linkId}</List.Header>
                      <List.List>
                        {displayQA(
                          chartResponseItems,
                          categoryForDisplay,
                          diffs,
                          chartAnswerByLinkId,
                        )}
                      </List.List>
                    </List.Content>
                  </List.Item>
                );
              })}
          </List.List>
        </div>
      );
    };

    const resolvePractitionerName = (practitionerId: string): string | null => {
      if (!practitionerId || !props.practitionerData) return null;
      const entry = props.practitionerData.find((p) => p.resource.id === practitionerId);
      if (!entry) return null;
      const name = entry.resource.name?.[0];
      return name?.text ?? (`${name?.given?.join(' ') ?? ''} ${name?.family ?? ''}`.trim() || null);
    };

    const getAddendumAuthor = (qrItems: QuestionnaireResponseItem[] | undefined): string | null => {
      if (!qrItems) return null;
      for (const group of qrItems) {
        if (group.linkId === ADDENDUM_FIELD_IDS.metadata && group.item) {
          for (const item of group.item) {
            if (item.linkId === ADDENDUM_FIELD_IDS.author && item.answer?.[0]?.valueString) {
              return item.answer[0].valueString;
            }
          }
        }
      }
      return null;
    };

    const renderAddendumSignature = (addendumRes: FhirQuestionnaireResponse) => {
      const items = addendumRes.item;
      const authored = addendumRes.authored;
      const authorId = getAddendumAuthor(items);
      const addendumAuthor =
        (authorId ? resolvePractitionerName(authorId) : null) || 'Unknown Author';
      const credPdf = findQuestionnaireResponseAnswerByLinkId(
        items,
        RD_SIGNATURE_CREDENTIALS_LINK_ID,
      );
      const signerWithCredentials = credPdf ? `${addendumAuthor}, ${credPdf}` : addendumAuthor;
      const cosignPractitionerId = getCosignPractitionerId(items);
      const cosignerName = cosignPractitionerId
        ? resolvePractitionerName(cosignPractitionerId)
        : null;
      const awaiting = isAwaitingCosign(items) && isSubmittedForCosign(addendumRes.extension);
      const isDraft = addendumRes.status === 'amended';
      const dateStr = `${moment(authored).local().format('MM/DD/YYYY')} at ${moment(authored).local().format('h:mm A z')}`;

      if (isDraft && awaiting) {
        return (
          <p style={{ fontStyle: 'italic', color: '#555', margin: '4px 0 8px' }}>
            Electronically signed by <strong>{signerWithCredentials}</strong> — pending co-sign
            {cosignerName && (
              <>
                {' '}
                assigned to <strong>{cosignerName}</strong>
              </>
            )}
          </p>
        );
      }

      if (isDraft && !awaiting) {
        return (
          <p style={{ fontStyle: 'italic', color: '#555', margin: '4px 0 8px' }}>
            Draft saved by <strong>{signerWithCredentials}</strong> on {dateStr}
          </p>
        );
      }

      if (cosignerName) {
        return (
          <p style={{ fontStyle: 'italic', color: '#555', margin: '4px 0 8px' }}>
            Electronically signed by <strong>{signerWithCredentials}</strong> and co-signed by{' '}
            <strong>{cosignerName}</strong> on {dateStr}
          </p>
        );
      }

      return (
        <p style={{ fontStyle: 'italic', color: '#555', margin: '4px 0 8px' }}>
          Electronically signed and completed by <strong>{signerWithCredentials}</strong> on{' '}
          {dateStr}
        </p>
      );
    };

    const displayAddenda = (parentQrId: string) => {
      const addenda = QuestionnaireResponseData?.entry?.filter((qr) => {
        return (
          getParentResponseId(qr.resource.extension) === parentQrId &&
          qr.resource.status !== 'entered-in-error'
        );
      });

      if (!addenda || addenda.length === 0) return null;

      return (
        <div style={{ marginTop: '20px', borderTop: '2px solid #eee', paddingTop: '10px' }}>
          <Header as="h3">Addenda</Header>
          {addenda.map((addendum, index) => {
            const isDraft = addendum.resource.status === 'amended';
            const awaiting =
              isAwaitingCosign(addendum.resource.item) &&
              isSubmittedForCosign(addendum.resource.extension);

            return (
              <Segment key={addendum.resource.id}>
                <Header as="h4" style={{ marginBottom: '4px' }}>
                  Addendum {index + 1}
                  {isDraft && awaiting && (
                    <Label
                      size="mini"
                      color="orange"
                      style={{ marginLeft: '8px', verticalAlign: 'middle' }}
                    >
                      <Icon name="clock outline" style={{ margin: 0 }} /> Awaiting Co-Sign
                    </Label>
                  )}
                  {isDraft && !awaiting && (
                    <Label
                      size="mini"
                      color="grey"
                      style={{ marginLeft: '8px', verticalAlign: 'middle' }}
                    >
                      Draft
                    </Label>
                  )}
                </Header>
                {renderAddendumSignature(addendum.resource)}
                <Button
                  size="mini"
                  color="teal"
                  basic
                  icon
                  labelPosition="right"
                  onClick={() => {
                    // eslint-disable-next-line @typescript-eslint/no-floating-promises -- fire-and-forget panel load; a rejection leaves the selected item's detail empty with no error shown
                    handleItemClick(addendum.resource.id!);
                    setCurrentQuestionnaire(null);
                  }}
                >
                  View Details
                  <Icon name="arrow right" />
                </Button>
              </Segment>
            );
          })}
        </div>
      );
    };

    return (
      <div>
        <div>
          <QuestionnairesList
            patientId={props.patientId}
            patientFirstName={props.patientFirstName}
            patientPhoneNumber={props.patientPhoneNumber}
            patientEmail={props.patientEmail}
            org={props.org}
            profileMutate={props.profileMutate}
            // `QuestionnairesList` declares `entry` REQUIRED — it filters it
            // unguarded — while this card declares it optional because it reads
            // it defensively. The hub's JSX guards on `questionnaireData.entry`
            // before rendering this card, which is what makes the pass sound.
            questionnaireData={props.questionnaireData as QuestionnaireBundle}
            refreshQA={refreshQA}
            setCurrentQuestionnaire={setCurrentQuestionnaire}
            setCurrentQuestionnaireResponse={setCurrentQuestionnaireResponse}
            setQuestionnaireTypesOrg={setQuestionnaireTypesOrg}
            language={surveyLanguage}
            handleLanguageSelect={handleLanguageSelect}
            handleTranslation={handleTranslation}
            langsList={languagesCareBetty}
            containsLang={containsLang}
            setContainsLang={setContainsLang}
            doNotContact={props.doNotContact}
            practitionerData={props.practitionerData}
            tasksData={props?.tasksData}
          />
        </div>

        <Divider />

        <List relaxed="very">
          <List.Item>
            {QuestionnaireResponseData && (
              <div>
                <Grid>
                  <Grid.Column
                    width={4}
                    style={{
                      maxHeight: '80vh',
                      overflowY: 'auto',
                      borderRight: '2px ridge',
                      overflowX: 'hidden',
                      maxWidth: '22%',
                      flex: '0 0 22%',
                    }}
                  >
                    <div>
                      <Dropdown
                        control={'select'}
                        scrolling
                        selection
                        placeholder="Filter by Questionnaire"
                        selectOnBlur={false}
                        clearable
                        // `QuestionnaireOption`'s three fields are each
                        // `string | undefined` (they come off `Questionnaire.id`
                        // and `.title`, both `0..1`) while Semantic's
                        // `DropdownItemProps` requires `key`/`value`. An
                        // untitled survey therefore contributes an option with
                        // no value — see the note on `QuestionnaireOption`.
                        options={questionnaireTypesOrg as DropdownItemProps[]}
                        // SUSPECTED DEFECT — REPORTED, NOT FIXED. Semantic's
                        // `DropdownProps['value']` does not admit `null`, and the
                        // unfiltered state of both these dropdowns IS `null` —
                        // `resetFilters` and the initial state both set it. A
                        // `null` value drops the Dropdown out of controlled mode
                        // for that render. Asserted to record what is passed today.
                        value={questionnaireResponseFilter.title as string}
                        onChange={(e) => handleQRFilter(e, 'title')}
                      />

                      <div style={{ height: '15px' }} />

                      <DatesRangeInput
                        name="datesRange"
                        placeholder="Filter by Date Range"
                        popupPosition="bottom left"
                        value={dateState}
                        iconPosition="right"
                        dateFormat="YYYY-MM-DD"
                        onChange={handleChangeDate}
                        clearable
                        style={{ width: '100%' }}
                      />

                      <div style={{ height: '15px' }} />

                      <Dropdown
                        control={'select'}
                        scrolling
                        selection
                        placeholder="Filter by Status"
                        selectOnBlur={false}
                        clearable
                        options={questionnaireStatus.map((status) => {
                          return { text: status, value: status, key: status };
                        })}
                        // `null` when unfiltered; see the note on the title filter.
                        value={questionnaireResponseFilter.status as string}
                        onChange={(e) => handleQRFilter(e, 'status')}
                      />

                      <div style={{ height: '15px' }} />

                      <Button
                        type="button"
                        content="Clear"
                        color="blue"
                        basic
                        onClick={resetFilters}
                      />

                      {!loader && !QuestionnaireResponseData.entry && (
                        <Message warning header="No Surveys Found!!!" />
                      )}

                      <List divided selection>
                        {QRData &&
                          QRData?.map((QRItem) => {
                            const currentQRHelper = new QuestionnaireResponseHelper(
                              QRItem?.resource,
                            );
                            const QRExtensionHelper = new QuestionnaireExtensionHelper(
                              currentQRHelper.getExtension(),
                            );

                            const authoredOn =
                              QRItem.resource.authored &&
                              moment(QRItem.resource.authored, 'YYYY-MM-DD')
                                .local(true)
                                .format('MMMM Do, YYYY');

                            const questionnaireId =
                              QRItem.resource.questionnaire &&
                              QRItem.resource.questionnaire.split('/')[1];

                            const isAddendum = !!getParentResponseId(QRItem.resource.extension);

                            let questionnaireDisplayTitle: string = '';
                            let questionnaireTitle: string = '';

                            if (props.questionnaireData?.entry) {
                              for (let i = 0; i < props.questionnaireData.entry.length; i++) {
                                if (
                                  questionnaireId === props.questionnaireData.entry[i]!.resource.id
                                ) {
                                  const QHelper = new QuestionnaireHelper(
                                    props.questionnaireData.entry[i]!.resource,
                                  );
                                  const QExtHelper = new QuestionnaireExtensionHelper(
                                    QHelper.getSurveyExtension(),
                                  );
                                  // Assigned and never read, but `getTitle()` is a call,
                                  // so dropping the statement changes what executes.
                                  // eslint-disable-next-line @typescript-eslint/no-unused-vars
                                  questionnaireTitle = QHelper.getTitle()!;

                                  // Kept as an expression statement: an `if` would emit different JavaScript.
                                  // eslint-disable-next-line @typescript-eslint/no-unused-expressions
                                  QExtHelper.getInternalTitle()
                                    ? (questionnaireDisplayTitle = QExtHelper.getInternalTitle()!)
                                    : (questionnaireDisplayTitle = QHelper.getTitle()!);

                                  break;
                                }
                              }
                            }

                            return (
                              (startRange
                                ? moment(startRange).format() <=
                                    moment(QRItem.resource.authored).format() &&
                                  moment(endRange).add(1, 'days').format() >
                                    moment(QRItem.resource.authored).format()
                                : true) &&
                              filterResponses(
                                questionnaireDisplayTitle,
                                questionnaireResponseFilter,
                                QRItem.resource.status,
                              ) &&
                              QRItem.resource.status !== 'entered-in-error' && (
                                <List.Item
                                  style={{
                                    cursor: 'pointer',
                                    backgroundColor:
                                      activeItem === QRItem.resource.id ? '#e7feff' : 'white',
                                    padding: '6px',
                                    borderRadius: '8px',
                                    margin: '12px 0px',
                                    boxShadow:
                                      '0 2px 5px 0 rgba(0, 0, 0, 0.16), 0 2px 10px 0 rgba(0, 0, 0, 0.12)',
                                    border:
                                      QRItem.resource.status.toString().toLowerCase() ===
                                      'completed'
                                        ? '2px solid green'
                                        : '',
                                  }}
                                  name={questionnaireId}
                                  key={QRItem.resource.id}
                                  className={
                                    activeItem === QRItem.resource.id ? 'selectedlistitem' : ''
                                  }
                                  active={activeItem === QRItem.resource.id}
                                  onClick={() => {
                                    // eslint-disable-next-line @typescript-eslint/no-floating-promises -- fire-and-forget panel load; a rejection leaves the selected item's detail empty with no error shown
                                    handleItemClick(QRItem.resource.id!);
                                    setCurrentQuestionnaire(null);
                                  }}
                                >
                                  <List.Content
                                    className={
                                      activeItem === QRItem.resource.id ? 'selectedlistitem' : ''
                                    }
                                  >
                                    <List.Header as="a">
                                      {questionnaireDisplayTitle}
                                      {isAddendum && (
                                        <Label
                                          size="mini"
                                          color="grey"
                                          style={{ marginLeft: '6px', verticalAlign: 'middle' }}
                                        >
                                          Addendum
                                        </Label>
                                      )}
                                      {QRItem.resource.status === 'amended' &&
                                        requiresCosignWorkflow(
                                          QRItem.resource.questionnaire!.substring(14),
                                        ) &&
                                        isAwaitingCosign(QRItem.resource.item) &&
                                        isSubmittedForCosign(QRItem.resource.extension) && (
                                          <Label
                                            size="mini"
                                            color="orange"
                                            style={{ marginLeft: '6px', verticalAlign: 'middle' }}
                                          >
                                            <Icon name="clock outline" style={{ margin: 0 }} />{' '}
                                            Co-Sign
                                          </Label>
                                        )}
                                    </List.Header>

                                    <List.Description as="a">
                                      {QRItem.resource.status.toString().toLowerCase() ===
                                      'completed'
                                        ? QRExtensionHelper.getCompletedDate()
                                          ? QRItem.resource.status.toString().toLowerCase() +
                                            ': ' +
                                            moment(QRExtensionHelper.getCompletedDate())
                                              .local(true)
                                              .format('MM/DD/YYYY')
                                          : QRItem.resource.status.toString().toLowerCase() +
                                            ': ' +
                                            moment(authoredOn, 'MMMM Do, YYYY')
                                              .local(true)
                                              .format('MM/DD/YYYY')
                                        : QRItem.resource.status.toString().toLowerCase() +
                                          ': ' +
                                          moment(authoredOn, 'MMMM Do, YYYY')
                                            .local(true)
                                            .format('MM/DD/YYYY')}
                                    </List.Description>

                                    <List.Description as="a">
                                      {QRExtensionHelper.getCompletedDate() ||
                                      QRExtensionHelper.getUpdateDate()
                                        ? QRExtensionHelper.getUpdateDate()
                                          ? 'updated: ' +
                                            moment(QRExtensionHelper.getUpdateDate())
                                              .local(true)
                                              .format('MM/DD/YYYY')
                                          : 'updated: ' +
                                            (moment(QRExtensionHelper.getCompletedDate())
                                              ? moment(moment(QRExtensionHelper.getCompletedDate()))
                                              : moment(authoredOn, 'MMMM Do, YYYY')
                                            )
                                              .local(true)
                                              .format('MM/DD/YYYY')
                                        : 'updated: ' +
                                          moment(authoredOn, 'MMMM Do, YYYY')
                                            .local(true)
                                            .format('MM/DD/YYYY')}
                                    </List.Description>

                                    <List.Description as="a">
                                      {getLastModifiedBy(QRItem.resource.identifier)}{' '}
                                      {QRItem.resource.status.toString().toLowerCase() ===
                                        'completed' && (
                                        <Icon name={'check circle'} color={'green'}></Icon>
                                      )}
                                    </List.Description>

                                    <List.Description as="em"></List.Description>
                                  </List.Content>
                                </List.Item>
                              )
                            );
                          })}
                      </List>
                    </div>
                  </Grid.Column>

                  <Grid.Column width={12}>
                    {/*
                      SUSPECTED DEFECT — REPORTED, NOT FIXED. Four props at this
                      boundary are declared narrower by the child than what this
                      producer can supply:

                      - `questionnaire` requires `id: string` and
                        `item: LooseQuestionnaireItem[]`; both are `0..1` on a
                        FHIR `Questionnaire`. The child reads `questionnaire.item`
                        unguarded to build its answer grid, so a Questionnaire
                        served without `item` throws rather than rendering empty.
                      - `currentQuestionnaireResponse` is declared as a whole
                        `QuestionnaireResponse`, but both producers here
                        (`duplicateSurvey` and the addendum prefill) build a
                        partial one with `id: ''` and no `meta`.
                      - `originalChartId`/`chainParentId` are `string | undefined`
                        on the child and `string | null` from `useAddendumState`.
                        `null` reaches a prop whose absence the child tests with
                        `?.`, which treats the two alike — so this one is inert.
                      - `allReferralData` — see the note on that prop above.

                      Asserted here so the pass happens exactly as it does today.
                    */}
                    {!loader && currentQuestionnaire && (
                      <Questionnaire
                        questionnaire={
                          currentQuestionnaire as FhirQuestionnaire & {
                            id: string;
                            item: LooseQuestionnaireItem[];
                          }
                        }
                        patientId={props.patientId}
                        refreshQA={refreshQA}
                        profileMutate={props.profileMutate}
                        currentQuestionnaireResponse={
                          currentQuestionnaireResponse as FhirQuestionnaireResponse
                        }
                        setCurrentQuestionnaire={setCurrentQuestionnaire}
                        setCurrentQuestionnaireResponse={setCurrentQuestionnaireResponse}
                        originalChartId={addendum.originalChartId}
                        chainParentId={addendum.chainParentId}
                        clearAddendumState={addendum.clearAddendumState}
                        language={surveyLanguage}
                        handleLanguageSelect={handleLanguageSelect}
                        langsList={languagesCareBetty}
                        org={props.org}
                        deleteLoader={loader}
                        consentDataMutate={props.consentDataMutate}
                        tasksMutate={props.tasksMutate}
                        careTeamData={props.careTeamData}
                        refreshTasks={props?.refreshTasks}
                        fhirPatient={props.fhirPatient}
                        refreshPatientDetails={props.refreshPatientDetails}
                        practitionerData={props.practitionerData}
                        questionnaireResponseMutate={props.questionnaireResponseMutate}
                        refreshQAForDraftSurveys={refreshQAForDraftSurveys}
                        questionnaireResponseData={props.questionnaireResponseData}
                        surveyImage={surveyImage}
                        allReferralData={props?.allReferralData}
                        tasksData={props?.tasksData}
                      />
                    )}

                    {!loader && !currentQuestionnaire && QuestionnaireResponseData.entry && (
                      <div
                        style={{
                          maxHeight: '80vh',
                          overflowY: 'auto',
                          position: 'relative',
                        }}
                      >
                        {loader && (
                          <Loader
                            active
                            inline="centered"
                            content={'Deleting survey response...'}
                          />
                        )}

                        <div style={{ marginBottom: '12px' }}>
                          {activeItem !== '' &&
                            QRDataObj?.[mapKey(activeItem)]?.status !== 'stopped' && (
                              <>
                                {lockedReadOnly ? (
                                  <>
                                    {canShowAddAddendum && (
                                      <Button
                                        style={{ marginLeft: '1%' }}
                                        size="tiny"
                                        color="orange"
                                        icon
                                        labelPosition="right"
                                        onClick={() => addAddendum()}
                                      >
                                        Add Addendum
                                        <Icon name="plus" />
                                      </Button>
                                    )}
                                    {pendingAddendumId && (
                                      <Button
                                        style={{ marginLeft: '1%' }}
                                        size="tiny"
                                        color="orange"
                                        icon
                                        labelPosition="right"
                                        onClick={() => handleItemClick(pendingAddendumId)}
                                      >
                                        Review Pending Addendum
                                        <Icon name="clock outline" />
                                      </Button>
                                    )}
                                  </>
                                ) : (
                                  <>
                                    {activeQrAwaitingCosign && isAssignedCosignerForActiveQr ? (
                                      <Button
                                        style={{ marginLeft: '1%' }}
                                        size="tiny"
                                        color="green"
                                        icon
                                        labelPosition="right"
                                        onClick={() => {
                                          // Same unguarded idiom as `duplicateSurvey`
                                          // above: the `&&` guards the element but
                                          // not the optional `entry` array, and
                                          // `qResponse` is dereferenced without a
                                          // test when no response matches.
                                          const qResponse =
                                            props.questionnaireResponseData.entry!.filter(
                                              (response) => response.resource.id === activeItem,
                                            )[0] &&
                                            props.questionnaireResponseData.entry!.filter(
                                              (response) => response.resource.id === activeItem,
                                            )[0]!.resource;

                                          setCurrentQuestionnaireResponse(qResponse);
                                          addendum.setStateFromResponse(qResponse!);

                                          let qrQuestionniare =
                                            props.questionnaireData.entry!.filter(
                                              (questionnaire) =>
                                                questionnaire.resource.id ===
                                                qResponse!.questionnaire!.substring(14),
                                            )[0] &&
                                            props.questionnaireData.entry!.filter(
                                              (questionnaire) =>
                                                questionnaire.resource.id ===
                                                qResponse!.questionnaire!.substring(14),
                                            )[0]!.resource;

                                          qrQuestionniare = addendum.prepareEditQuestionnaire(
                                            qResponse!,
                                            qrQuestionniare,
                                            props.questionnaireData,
                                          );

                                          setCurrentQuestionnaire(qrQuestionniare);
                                        }}
                                      >
                                        Co-Sign & Complete
                                        <Icon name="check" />
                                      </Button>
                                    ) : (
                                      <Button
                                        style={{ marginLeft: '1%' }}
                                        size="tiny"
                                        color="green"
                                        icon
                                        labelPosition="right"
                                        onClick={() => {
                                          // Same unguarded idiom as the Co-Sign
                                          // handler above.
                                          const qResponse =
                                            props.questionnaireResponseData.entry!.filter(
                                              (response) => response.resource.id === activeItem,
                                            )[0] &&
                                            props.questionnaireResponseData.entry!.filter(
                                              (response) => response.resource.id === activeItem,
                                            )[0]!.resource;

                                          setCurrentQuestionnaireResponse(qResponse);
                                          addendum.setStateFromResponse(qResponse!);

                                          let qrQuestionniare =
                                            props.questionnaireData.entry!.filter(
                                              (questionnaire) =>
                                                questionnaire.resource.id ===
                                                qResponse!.questionnaire!.substring(14),
                                            )[0] &&
                                            props.questionnaireData.entry!.filter(
                                              (questionnaire) =>
                                                questionnaire.resource.id ===
                                                qResponse!.questionnaire!.substring(14),
                                            )[0]!.resource;

                                          qrQuestionniare = addendum.prepareEditQuestionnaire(
                                            qResponse!,
                                            qrQuestionniare,
                                            props.questionnaireData,
                                          );

                                          setCurrentQuestionnaire(qrQuestionniare);
                                        }}
                                      >
                                        Edit
                                        <Icon name="edit" />
                                      </Button>
                                    )}

                                    <Button
                                      style={{ marginLeft: '1%' }}
                                      size="tiny"
                                      color="purple"
                                      icon
                                      labelPosition="right"
                                      onClick={() => duplicateSurvey()}
                                    >
                                      Duplicate
                                      <Icon name="copy outline" />
                                    </Button>

                                    <Button
                                      style={{ marginLeft: '1%' }}
                                      size="tiny"
                                      color="red"
                                      icon
                                      labelPosition="right"
                                      onClick={() => setDeleteConfirm(true)}
                                    >
                                      Delete
                                      <Icon name="trash alternate" />
                                    </Button>
                                  </>
                                )}

                                {(() => {
                                  const activeRes = findResponseData(activeItem)?.resource;
                                  const activeParentId = activeRes
                                    ? getParentResponseId(activeRes.extension)
                                    : null;
                                  return activeParentId ? (
                                    <Button
                                      style={{ marginLeft: '1%' }}
                                      size="tiny"
                                      color="teal"
                                      icon
                                      labelPosition="right"
                                      onClick={() => {
                                        // eslint-disable-next-line @typescript-eslint/no-floating-promises -- fire-and-forget panel load; a rejection leaves the selected item's detail empty with no error shown
                                        handleItemClick(activeParentId);
                                        setCurrentQuestionnaire(null);
                                      }}
                                    >
                                      View Parent Chart
                                      <Icon name="arrow up" />
                                    </Button>
                                  ) : null;
                                })()}

                                <Button
                                  icon
                                  size="mini"
                                  color="green"
                                  labelPosition="right"
                                  onClick={handleDownloadClick}
                                  title="Download PDF"
                                >
                                  Download
                                  <Icon name="download" />
                                </Button>
                              </>
                            )}
                        </div>

                        {error.length > 0 && (
                          <Message negative>
                            <Message.Header>{error}</Message.Header>
                          </Message>
                        )}

                        <div
                          style={{
                            background: '#fff',
                            padding: '8px',
                          }}
                        >
                          <h3
                            style={{
                              marginTop: '1.5%',
                              marginBottom: '0%',
                              display: 'inline',
                            }}
                          >
                            {activeItem !== '' &&
                              QRDataObj[mapKey(activeItem)] &&
                              QRDataObj[mapKey(activeItem)]!.header}
                          </h3>

                          <div style={{ marginTop: '1%', display: 'inline' }}>
                            {activeItem !== '' &&
                              QRDataObj[mapKey(activeItem)] &&
                              displayCategories(
                                QRDataObj[mapKey(activeItem)]!.questions,
                                questionnaires[QRDataObj[mapKey(activeItem)]!.questionnaire],
                              )}
                          </div>

                          {activeItem !== '' && displayChartCopy(activeItem!)}

                          {activeItem !== '' && displayAddenda(activeItem!)}
                        </div>
                      </div>
                    )}

                    {QuestionnaireResponseData && activeItem === '' && loader && (
                      <Loader active inline="centered" content={'Loading survey response...'} />
                    )}
                  </Grid.Column>
                </Grid>
              </div>
            )}
          </List.Item>

          <Confirm
            content={
              isRdConsultChart(QRDataObj?.[mapKey(activeItem)]?.questionnaire as string)
                ? 'Are you sure you want to delete this RD consult chart? All linked addendums will also be deleted.'
                : 'Are you sure you want to delete this survey?'
            }
            open={deleteConfirm}
            closeOnDimmerClick={false}
            onCancel={() => setDeleteConfirm(false)}
            onConfirm={() => deleteSurvey(activeItem!)}
          />
        </List>
      </div>
    );
  }

  // Unreachable: the branches above are exhaustive over `QuestionnaireResponseData`.
  // Returning `null` rather than falling through to `undefined` keeps the return type
  // valid JSX; both render nothing.
  return null;
}

export default QuestionnaireResponseCard;
